import * as Yup from "yup";

export const AllTeamSchemas = Yup.object({
  name: Yup.string().required("Team Name is Required"),
  email: Yup.string().email().required("Email  is Required"),
  //address: Yup.string().required("Address is Required"),
  city: Yup.string().required("City is Required"),
  state: Yup.string().required("State is Required"),
  zipCode: Yup.string().required("Zip Code is Required"),
});

// Converts "" from empty inputs into null so optional fields can be left blank
const emptyToNull = (value, originalValue) =>
  originalValue === "" || originalValue === undefined ? null : value;

export const AllTournamentSchemas = Yup.object({
  name: Yup.string().trim().required("Tournament Name is Required"),
  startDate: Yup.date()
    .typeError("Start Date is Required")
    .required("Start Date is Required"),
  endDate: Yup.date()
    .typeError("End Date is Required")
    .required("End Date is Required")
    .min(Yup.ref("startDate"), "End Date can't be before Start Date"),
  tournamentStatus: Yup.string().required("Tournament Status is Required"),
  sportId: Yup.number()
    .typeError("Sport is Required")
    .required("Sport is Required"),

  // ── numberOfDivisions is only required for TEAM tournaments, not DRAFT ──
  numberOfDivisions: Yup.number()
    .transform(emptyToNull)
    .typeError("No Of Division must be a valid number")
    .nullable()
    .when("tournamentType", {
      is: (type) => type !== "DRAFT",
      then: (schema) =>
        schema.required("Number Of Divisions is Required").min(1, "Must be at least 1"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  // ── publicSlug is required only for DRAFT tournaments ──
  publicSlug: Yup.string()
    .nullable()
    .when("tournamentType", {
      is: "DRAFT",
      then: (schema) =>
        schema
          .required("Public URL Slug is required for Draft tournaments")
          .matches(
            /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
            "Slug must be lowercase letters, numbers and hyphens only (e.g. summer-classic-2025)"
          ),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  rosterDueDate: Yup.date()
    .transform(emptyToNull)
    .typeError("Roster Due Date must be a valid date")
    .nullable()
    .when("startDate", {
      is: (start) => start instanceof Date && !isNaN(start),
      then: (schema) =>
        schema.max(Yup.ref("startDate"), "Roster Due Date can't be after Start Date"),
    }),

  maxPlayersPerRoster: Yup.number()
    .transform(emptyToNull)
    .typeError("Must be a valid number")
    .nullable()
    .positive("Must be positive"),
  minPlayersPerRoster: Yup.number()
    .transform(emptyToNull)
    .typeError("Must be a valid number")
    .nullable()
    .positive("Must be positive")
    .test("min-lte-max", "Min Players can't be more than Max Players", function (value) {
      const { maxPlayersPerRoster } = this.parent;
      return value == null || maxPlayersPerRoster == null || value <= maxPlayersPerRoster;
    }),

  loyaltyPointsPerRegistration: Yup.number()
    .transform(emptyToNull)
    .typeError("Must be a valid number")
    .nullable()
    .min(0, "Can't be negative")
    .max(9999, "Max 9999 points"),

  cancellationDeadlineDays: Yup.number()
    .transform(emptyToNull)
    .typeError("Must be a valid number")
    .nullable()
    .min(0, "Can't be negative"),
});


export const AllVenueSchemas = Yup.object({
  name: Yup.string().required("Venue Name is Required"),
  address1: Yup.string().required("Address1 is Required"),
  city: Yup.string().required("City is Required"),
  state: Yup.string().required("State is Required"),
  zipCode: Yup.string().required("Zip Code is Required"),
  numberOfFields: Yup.number().required("Number of Fields is Required"),
  venueStatus: Yup.string().required("Venue Status is Required"),
});

export const AdminValuesSchemas = Yup.object({
  email: Yup.string().email().required("Email is Required"),
  firstName: Yup.string().required("first Name is Required"),
  lastName: Yup.string().required("last Name is Required"),
});

export const ManagerUpdateValuesSchemas = Yup.object({
  email: Yup.string().email().required("Email is Required"),
  firstName: Yup.string().required("first Name is Required"),
  lastName: Yup.string().required("last Name is Required"),
  mobilePhone: Yup.string().required("Mobile Phone is Required"),
});

export const AllDivisionSchemas = Yup.object({
  divisionName: Yup.string().required("Division Name is Required"),

  entryFee: Yup.number()
    .typeError("Entry Fee must be a valid number")
    .required("Entry Fee is Required")
    .test("is-greater", "Entry Fee must be greater than Initial Deposit Fee", function (value) {
      return value > this.parent.initialDepositFee;
    }),
  initialDepositFee: Yup.number()
    .typeError("Initial Deposit Fee must be a valid number")
    .required("Initial Deposit Fee is Required"),
  divisionStatus: Yup.string().required("Division Status is Required"),
  maxTeams: Yup.number()
    .min(1, "Max Teams must be at least 1")
    .typeError("Max Teams must be a valid number")
    .required("Max Teams is Required"),
  startTime: Yup.string().required("Start Time is Required"),
  prize1: Yup.string().typeError("Prize 1 must be a valid value").required("Prize 1 is Required"),
  prize2: Yup.string().typeError("Prize 1 must be a valid value").nullable(),
  prize3: Yup.string().typeError("Prize 1 must be a valid value").nullable(),
  prize4: Yup.string().typeError("Prize 1 must be a valid value").nullable(),
  tournamentId: Yup.number()
    .typeError("Please Select Tournament")
    .required("Tournament is Required"),
});


export const AllUsersSchema = Yup.object({
  name: Yup.string().required("Name is Required"),
  role: Yup.string().required("Role is Required"),
  email: Yup.string().email().required("Email is Required"),
  firstName: Yup.string().required("First Name is Required"),
  lastName: Yup.string().required("Last Name  is Required"),
  password: Yup.string().required("Password  is Required"),
  address1: Yup.string().required("Address1 is Required"),
  division: Yup.string().required("Division is Required"),
  points: Yup.number().nullable().required("Points is Required"),
  ranking: Yup.number().nullable().required("Ranking is Required"),
  city: Yup.string().required("City is Required"),
  state: Yup.string().required("State is Required"),
  zipCode: Yup.string().required("Zip Code is Required"),
  tournamentsPlayed: Yup.number()
    .nullable()
    .required("Tournaments Played is Required"),
  playerStatus: Yup.string().required("Player Status is Required"),
  gamesPlayed: Yup.number().nullable().required("Games Played is Required"),
  mobilePhone: Yup.string().required("Mobile Phone is Required"),
});

export const ProviderRegisterSchemas = Yup.object({
  firstName: Yup.string().required("First Name is Required"),
  lastName: Yup.string().required("Last Name  is Required"),
  email: Yup.string().email().required("Email is Required"),
  address1: Yup.string().required("Address 1 is Required"),
  city: Yup.string().required("City is Required"),
  state: Yup.string().required("State is Required"),
  zipCode: Yup.string().required("Zip Code is Required"),
  mobilePhone: Yup.string()
    .required("Mobile Phone is Required")
    .matches(
      /^[+]?[0-9\s-]+$/,
      "Invalid mobile phone format. Only numbers, spaces, '+' or '-' are allowed."
    ),
  password: Yup.string().required("Password is Required"),
  confirmPassword: Yup.string()
    .required("Confirm Password is Required")
    .oneOf([Yup.ref("password"), null], "Passwords must match"),
  role: Yup.string().required("Role is required"),
});
export const ForgetPasswordSchema = Yup.object({
  email: Yup.string().email().required("Email is Required"),
});

export const AddPersonSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  role: Yup.string().required("Role is required"),
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  mobilePhone: Yup.string().required("Mobile Phone is required"),
  //driverLicenseId: Yup.string().required("Driver License Id is required"),
  //dateOfBirth: Yup.string().required("Date of Birth is required"),
});

// Sport Schema
export const AddSportSchema = Yup.object({
  name: Yup.string()
    .min(2, "Sport name must be at least 2 characters")
    .max(100, "Sport name must be less than 100 characters")
    .required("Sport name is required"),
  sportStatus: Yup.string()
    .oneOf(["ACTIVE", "INACTIVE", "CLOSED"], "Invalid status")
    .required("Status is required"),
});

// Division Schema
export const AddDivisionSchema = Yup.object({
  name: Yup.string()
    .min(2, "Division Master name must be at least 2 characters")
    .max(100, "Division Master name must be less than 100 characters")
    .required("Division Master name is required"),
  category: Yup.string()
    .min(2, "Category must be at least 2 characters")
    .max(100, "Category must be less than 100 characters")
    .required("Category is required"),
  status: Yup.string()
    .oneOf(["ACTIVE", "INACTIVE", "CLOSED"], "Invalid status")
    .required("Status is required"),
  sportId: Yup.number()
    .positive("Please select a valid sport")
    .required("Sport is required"),
});
